import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('hi-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };

const STATE_BADGE = {
  paid: 'bg-green-100 text-green-700',
  trial: 'bg-blue-100 text-blue-700',
  demo: 'bg-amber-100 text-amber-700',
};

const Kpi = ({ label, value, color, testid }) => (
  <div className={`rounded-xl p-4 ${color}`} data-testid={testid}>
    <p className="text-2xl font-bold">{value}</p>
    <p className="text-[11px] opacity-80 mt-0.5">{label}</p>
  </div>
);

const RecordPaymentForm = ({ schoolId, onDone, authToken }) => {
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('upi');
  const [note, setNote] = useState('');
  const [markPaid, setMarkPaid] = useState(true);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!amount || Number(amount) <= 0) { toast.error('सही राशि डालें'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/admin/school/${schoolId}/record-payment`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount_inr: Number(amount), method, note, mark_paid: markPaid }),
      });
      const d = await res.json();
      if (d.success) { toast.success('✅ Payment दर्ज हो गया' + (markPaid ? ' · account active' : '')); onDone(); }
      else toast.error(d.detail || 'विफल');
    } catch (e) { toast.error('Error'); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4 space-y-3" data-testid="record-payment-form">
      <p className="text-sm font-bold text-slate-700">💵 Payment दर्ज करें (offline)</p>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="राशि ₹"
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none" data-testid="record-amount" />
        <select value={method} onChange={(e) => setMethod(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none" data-testid="record-method">
          <option value="upi">UPI</option>
          <option value="cash">Cash</option>
          <option value="bank_transfer">Bank Transfer</option>
          <option value="cheque">Cheque</option>
          <option value="other">Other</option>
        </select>
      </div>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
        className="w-full px-3 py-2 text-sm rounded-lg border border-slate-200 outline-none" data-testid="record-note" />
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={markPaid} onChange={(e) => setMarkPaid(e.target.checked)} data-testid="record-markpaid" />
        Payment दर्ज होते ही account को "Paid / Active" मार्क करें
      </label>
      <button onClick={submit} disabled={saving}
        className="w-full py-2 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50" data-testid="record-submit">
        {saving ? '⏳...' : 'Payment दर्ज करें'}
      </button>
    </div>
  );
};

const SchoolDrawer = ({ schoolId, onClose, authToken, refresh }) => {
  const [data, setData] = useState(null);
  const [activating, setActivating] = useState(false);
  const [trialOn, setTrialOn] = useState(false);
  const [togglingTrial, setTogglingTrial] = useState(false);
  const load = useCallback(async () => {
    const res = await fetch(`${API_URL}/api/billing/admin/school/${schoolId}`, { headers: { Authorization: `Bearer ${authToken}` } });
    const d = await res.json();
    setData(d);
    setTrialOn(!!(d?.billing?.trial_enabled));
  }, [schoolId, authToken]);
  useEffect(() => { load(); }, [load]);
  const b = data?.billing;

  const toggleTrial = async () => {
    setTogglingTrial(true);
    try {
      const res = await fetch(`${API_URL}/api/activation/admin/school-trial-toggle/${schoolId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !trialOn }),
      });
      const d = await res.json();
      if (d.success) { setTrialOn(d.trial_enabled); toast.success(d.message); refresh(); }
      else toast.error(d.detail || 'Toggle विफल');
    } catch (e) { toast.error('Toggle error'); }
    finally { setTogglingTrial(false); }
  };

  const activateSchool = async () => {
    if (!window.confirm('इस school का demo data clear होकर Trial शुरू होगा और school admin को notification जाएगा। आगे बढ़ें?')) return;
    setActivating(true);
    try {
      const res = await fetch(`${API_URL}/api/activation/admin/activate-school/${schoolId}`, {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = await res.json();
      if (d.success) {
        toast.success(d.already_active ? 'यह school पहले से active है' : `🎉 Activated! ${d.trial_days || 14}-दिन का trial शुरू${d.notified ? ' · WhatsApp भेजा गया' : ''}`);
        load(); refresh();
      } else toast.error(d.detail || 'Activation विफल');
    } catch (e) { toast.error('Activation error'); }
    finally { setActivating(false); }
  };

  const downloadReceipt = async (pid) => {
    try {
      const res = await fetch(`${API_URL}/api/billing/admin/school/${schoolId}/receipt/${pid}`, { headers: { Authorization: `Bearer ${authToken}` } });
      if (!res.ok) { toast.error('Receipt नहीं मिला'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `receipt_${pid.slice(0, 8)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error('Download error'); }
  };

  const waReceipt = async (pid) => {
    try {
      const res = await fetch(`${API_URL}/api/billing/admin/school/${schoolId}/receipt/${pid}/whatsapp`, { method: 'POST', headers: { Authorization: `Bearer ${authToken}` } });
      const d = await res.json();
      if (d.success && d.sent) toast.success('📲 Receipt WhatsApp पर भेजा गया');
      else toast.message('WhatsApp भेजा नहीं जा सका (24h window/नंबर जाँचें)');
    } catch (e) { toast.error('WhatsApp error'); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="billing-school-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-4 flex justify-between items-center">
          <p className="font-bold text-sm truncate">{b?.school_name || 'School'}</p>
          <button onClick={onClose} className="text-xl leading-none" data-testid="drawer-close">×</button>
        </div>
        {!b ? <p className="p-6 text-center text-slate-400">⏳...</p> : (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 gap-2 text-center">
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-lg font-bold">{b.student_count}</p><p className="text-[10px] text-slate-500">Students</p></div>
              <div className="bg-slate-50 rounded-lg p-3"><p className="text-lg font-bold">{b.is_custom ? 'Custom' : inr(b.one_time_price)}</p><p className="text-[10px] text-slate-500">One-time · {b.total_users} users</p></div>
              <div className="bg-emerald-50 rounded-lg p-3"><p className="text-lg font-bold text-emerald-700">{b.is_custom ? '—' : inr(b.annual_maintenance)}</p><p className="text-[10px] text-slate-500">Maintenance/yr</p></div>
              <div className="bg-rose-50 rounded-lg p-3"><p className="text-lg font-bold text-rose-700">{inr(b.amount_due)}</p><p className="text-[10px] text-slate-500">Due</p></div>
            </div>
            <RecordPaymentForm schoolId={schoolId} authToken={authToken} onDone={() => { load(); refresh(); }} />
            <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center justify-between gap-3" data-testid="trial-toggle-box">
              <div className="min-w-0">
                <p className="text-sm font-bold text-slate-700">🎁 Trial अनुमति (इस school के लिए)</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {trialOn
                    ? 'चालू — इस school के users "Activate My Trial" से Free trial शुरू कर सकते हैं।'
                    : 'बंद — school को Guided Demo + payment-first दिखेगा (default)।'}
                </p>
              </div>
              <button onClick={toggleTrial} disabled={togglingTrial}
                className={`shrink-0 w-14 h-7 rounded-full relative transition ${trialOn ? 'bg-emerald-500' : 'bg-slate-300'} disabled:opacity-50`}
                data-testid="trial-toggle-btn" aria-label="trial toggle">
                <span className={`absolute top-0.5 ${trialOn ? 'left-8' : 'left-0.5'} w-6 h-6 bg-white rounded-full shadow transition-all flex items-center justify-center text-[9px] font-bold text-slate-600`}>
                  {togglingTrial ? '⏳' : (trialOn ? 'ON' : 'OFF')}
                </span>
              </button>
            </div>
            {(b.account_state === 'demo' && trialOn) && (
              <button onClick={activateSchool} disabled={activating}
                className="w-full py-2.5 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50" data-testid="activate-school-btn">
                {activating ? '⏳ Activating...' : '🚀 Activate School (Demo data clear + Trial शुरू)'}
              </button>
            )}
            <div>
              <p className="text-sm font-bold text-slate-700 mb-2">📜 Payment History</p>
              {(!data.history || data.history.length === 0) ? (
                <p className="text-xs text-slate-400" data-testid="drawer-no-history">अभी कोई payment नहीं।</p>
              ) : (
                <div className="space-y-2" data-testid="drawer-history">
                  {data.history.map((h, i) => (
                    <div key={i} className="flex justify-between items-center border border-slate-100 rounded-lg p-2 text-xs">
                      <div><span className="font-semibold">{inr(h.amount_inr)}</span> · {h.method || h.payment_type} {h.is_manual && '✍️'}</div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <span className={`px-1.5 py-0.5 rounded ${h.status === 'success' || h.status === 'paid' ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-500'}`}>{h.status}</span>
                          <p className="text-[10px] text-slate-400">{fmtDate(h.created_at)}</p>
                        </div>
                        {['success', 'paid', 'captured'].includes(h.status) && (
                          <div className="flex flex-col gap-1">
                            <button onClick={() => downloadReceipt(h.id)} className="text-[10px] px-2 py-0.5 rounded bg-slate-700 text-white" data-testid={`receipt-dl-${h.id}`}>📄 PDF</button>
                            <button onClick={() => waReceipt(h.id)} className="text-[10px] px-2 py-0.5 rounded bg-green-500 text-white" data-testid={`receipt-wa-${h.id}`}>📲 WA</button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default function BillingAdminPage({ token }) {
  const authToken = token || localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(null);

  const [reminding, setReminding] = useState(null);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return;   // StrictMode / rapid re-mount guard — avoid duplicate concurrent fetches
    inFlight.current = true;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/admin/overview`, { headers: { Authorization: `Bearer ${authToken}` } });
      const d = await res.json();
      setData(res.ok && Array.isArray(d.schools) ? d : { schools: [], kpis: {} });
    } catch (e) { toast.error('Billing load नहीं हुआ'); setData({ schools: [], kpis: {} }); }
    finally { setLoading(false); inFlight.current = false; }
  }, [authToken]);
  useEffect(() => { load(); }, [load]);

  const sendReminder = async (sid) => {
    setReminding(sid);
    try {
      const res = await fetch(`${API_URL}/api/billing/admin/school/${sid}/send-reminder`, {
        method: 'POST', headers: { Authorization: `Bearer ${authToken}` },
      });
      const d = await res.json();
      if (d.success) toast.success(d.sent ? '🔔 Reminder WhatsApp + in-app भेज दिया गया' : '🔔 In-app reminder भेजा (WhatsApp नहीं — 24h window/नंबर जाँचें)');
      else toast.error(d.detail || 'Reminder विफल');
    } catch (e) { toast.error('Reminder error'); }
    finally { setReminding(null); }
  };

  if (loading) return <div className="p-6 text-center text-slate-400" data-testid="billing-admin-loading">⏳ लोड हो रहा है...</div>;
  const k = data?.kpis || {};

  return (
    <div className="space-y-5" data-testid="billing-admin-page">
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl p-5 text-white">
        <h2 className="text-lg font-bold">💳 Billing & Payments</h2>
        <p className="text-sm text-white/90 mt-1">सभी schools का billing एक जगह — Slab model (one-time + सालाना maintenance, कुल users पर तय)।</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi label="Collected Revenue" value={inr(k.collected_revenue)} color="bg-green-50 text-green-700" testid="kpi-collected" />
        <Kpi label="Pending Amount" value={inr(k.pending_amount)} color="bg-rose-50 text-rose-700" testid="kpi-pending" />
        <Kpi label={`⚠️ Overdue Schools`} value={k.overdue || 0} color="bg-orange-50 text-orange-700" testid="kpi-overdue" />
        <Kpi label="Potential ARR" value={inr(k.potential_arr)} color="bg-indigo-50 text-indigo-700" testid="kpi-arr" />
        <Kpi label="Paid / Trial / Demo" value={`${k.paid}/${k.trial}/${k.demo}`} color="bg-slate-100 text-slate-700" testid="kpi-states" />
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="billing-schools-table">
            <thead className="bg-slate-50 text-slate-500 text-xs">
              <tr>
                <th className="text-left p-3">School</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Students</th>
                <th className="text-right p-3">Annual</th>
                <th className="text-right p-3">Paid</th>
                <th className="text-right p-3">Due</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {(data.schools || []).map((r) => (
                <tr key={r.school_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`billing-row-${r.school_id}`}>
                  <td className="p-3">
                    <p className="font-semibold text-slate-800">{r.school_name}</p>
                    {r.tier_label && <span className="text-[10px] text-slate-400">{r.tier_label}{r.is_custom ? ' · Custom' : ''}</span>}
                    {r.is_overdue && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700" data-testid={`overdue-badge-${r.school_id}`}>⚠️ Overdue</span>}
                  </td>
                  <td className="p-3 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full ${STATE_BADGE[r.account_state] || 'bg-slate-100'}`}>{r.account_state}</span></td>
                  <td className="p-3 text-right">{r.student_count}</td>
                  <td className="p-3 text-right font-medium">{inr(r.annual_amount)}</td>
                  <td className="p-3 text-right text-green-600">{inr(r.paid_total)}</td>
                  <td className="p-3 text-right text-rose-600 font-semibold">{inr(r.amount_due)}</td>
                  <td className="p-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {r.is_overdue && (
                        <button onClick={() => sendReminder(r.school_id)} disabled={reminding === r.school_id}
                          className="text-xs px-2.5 py-1.5 rounded-lg bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50" data-testid={`reminder-${r.school_id}`}>
                          {reminding === r.school_id ? '⏳' : '🔔 Reminder'}
                        </button>
                      )}
                      <button onClick={() => setSel(r.school_id)} className="text-xs px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700" data-testid={`manage-${r.school_id}`}>Manage</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {sel && <SchoolDrawer schoolId={sel} authToken={authToken} onClose={() => setSel(null)} refresh={load} />}
    </div>
  );
}
